import { useRef, useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";

interface FaqItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}

/**
 * FaqItem — Smooth accordion FAQ item with slide + fade animation.
 * Uses max-height transition for smooth open/close without layout jumps.
 */
export default function FaqItem({ question, answer, isOpen, onToggle }: FaqItemProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setMaxHeight(isOpen ? contentRef.current.scrollHeight : 0);
    }
  }, [isOpen, answer]);

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-colors">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-4 text-left group"
        aria-expanded={isOpen}
      >
        <span className="text-gray-900 font-medium pr-4 group-hover:text-[#2563EB] transition-colors">
          {question}
        </span>
        <ChevronDown
          className={`w-5 h-5 text-[#2563EB] shrink-0 transition-transform duration-300 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      <div
        ref={contentRef}
        style={{ maxHeight: `${maxHeight}px` }}
        className="overflow-hidden transition-all duration-400 ease-in-out"
      >
        <div className="px-6 pb-5">
          <div className="h-px bg-gray-100 mb-4" />
          <p
            className={`text-gray-500 text-sm leading-relaxed transition-opacity duration-300 ${
              isOpen ? "opacity-100" : "opacity-0"
            }`}
          >
            {answer}
          </p>
        </div>
      </div>
    </div>
  );
}
